import { useState } from "react";
import API, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileUp, Loader2, CheckCircle, AlertTriangle, FileSpreadsheet } from "lucide-react";

export default function AdminReagentMapping() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/admin/reagent-mapping/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl animate-slide-up" data-testid="admin-reagent-mapping">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Reagent Mapping Import</h2>
        <p className="text-sm text-gray-500 mt-0.5">Bulk-update reagent catalog from Excel/CSV. Existing image, description and featured-flag stay intact — only mapping, MRP, customer price, pack size and instruments get refreshed.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex gap-2">
        <FileSpreadsheet className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Expected columns (header row required):</p>
          <p className="font-mono text-[11px] mt-1">Segement · Reagent Type · Instrument Code · Reagent Code · Reagent Name · Reagent Pack Size in ML · Reagent Customer Price · MRP</p>
          <p className="text-[11px] mt-1">Reagent Type values: <span className="font-mono">Open Reagents / Close Reagents / QC &amp; Calibrators / ELISA / Rapids</span>. Same Reagent Code can repeat across rows to map to multiple instruments.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Select Excel / CSV file</span>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); setError(""); }}
            className="mt-2 block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[#E0F4F5] file:text-[#008A99] hover:file:bg-[#008A99]/15 cursor-pointer"
            data-testid="reagent-mapping-file"
          />
        </label>
        {file && (
          <p className="text-xs text-slate-500" data-testid="selected-file">
            Selected: <span className="font-semibold text-slate-800">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
          </p>
        )}
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full md:w-auto bg-[#008A99] hover:bg-[#006670] text-white rounded-xl shadow-lg shadow-[#008A99]/20"
          data-testid="upload-reagent-mapping"
        >
          {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <><FileUp className="w-4 h-4 mr-2" />Import Mapping</>}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex gap-2" data-testid="import-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-2" data-testid="import-result">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
            <CheckCircle className="w-5 h-5" />
            {result.message || "Import complete"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Rows Parsed</p>
              <p className="text-lg font-bold text-slate-800">{result.rows_parsed}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Unique Reagents</p>
              <p className="text-lg font-bold text-slate-800">{result.unique_reagents}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Updated</p>
              <p className="text-lg font-bold text-emerald-600">{result.updated}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Created</p>
              <p className="text-lg font-bold text-sky-600">{result.created}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
